import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Mic, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button.tsx";
import { apiVoiceAdd, fetchVoiceStatus, VoiceJobStatus } from "@/api/api.ts";
import { markAiItems, summarizeVoiceResult } from "@/api/voice.ts";
import { addStateEntry, itemsState, listsState, updateTaskCounts } from "@/states.ts";

const STAGE_LABEL: Record<string, string> = {
	queued: "Uploading...",
	transcribing: "Transcribing...",
	parsing: "Understanding...",
};

const VoiceAddButton: React.FC = () => {
	const [recording, setRecording] = useState(false);
	const [busy, setBusy] = useState(false);
	const [stage, setStage] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const mediaRecorder = useRef<MediaRecorder | null>(null);
	const chunks = useRef<Blob[]>([]);

	useEffect(() => {
		return () => {
			mediaRecorder.current?.stream.getTracks().forEach((t) => t.stop());
		};
	}, []);

	useEffect(() => {
		if (!notice) return;
		const timer = setTimeout(() => setNotice(null), 5000);
		return () => clearTimeout(timer);
	}, [notice]);

	const blobToBase64 = (blob: Blob) =>
		new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const result = reader.result as string;
				resolve(result.split(",")[1] ?? "");
			};
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});

	const applyResult = useCallback((status: VoiceJobStatus) => {
		const result = status.result;
		if (!result) return;
		markAiItems(result.items.map((i) => i.id));
		setNotice(summarizeVoiceResult(result));
		for (const item of result.items) {
			const targetListId = item.list_id ?? "";
			if (!targetListId) continue;
			if (!itemsState.items[targetListId]) itemsState.items[targetListId] = [];
			addStateEntry(itemsState.items[targetListId], item, true);
			updateTaskCounts(targetListId);
		}
		for (const lst of result.lists) {
			addStateEntry(listsState.lists, lst, true);
		}
	}, []);

	const waitForResult = useCallback(async (jobId: string) => {
		while (true) {
			const status = await fetchVoiceStatus(jobId);
			if (status.status === "done") {
				applyResult(status);
				setBusy(false);
				setStage(null);
				return;
			}
			if (status.status === "error") {
				setError(status.error || "Voice failed");
				setBusy(false);
				setStage(null);
				return;
			}
			setStage(status.stage ?? null);
			await new Promise((r) => setTimeout(r, 1500));
		}
	}, [applyResult]);

	const handleClick = async () => {
		setError(null);
		if (recording) {
			mediaRecorder.current?.stop();
			return;
		}

		const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
		const recorder = new MediaRecorder(stream);
		chunks.current = [];
		recorder.ondataavailable = (e) => {
			if (e.data.size > 0) chunks.current.push(e.data);
		};
		recorder.onstop = async () => {
			stream.getTracks().forEach((t) => t.stop());
			setRecording(false);
			if (chunks.current.length === 0) return;

			setBusy(true);
			setStage("queued");
			try {
				const audioBase64 = await blobToBase64(new Blob(chunks.current, { type: "audio/webm" }));
				const res = await apiVoiceAdd(audioBase64);
				if (!res.ok) {
					setError("Something went wrong");
					setBusy(false);
					setStage(null);
					return;
				}
				await waitForResult(res.job_id);
			} catch (e: any) {
				setError(e?.response?.data?.error || "Voice failed");
				setBusy(false);
				setStage(null);
			}
		};
		recorder.start();
		mediaRecorder.current = recorder;
		setRecording(true);
	};

	return (
		<div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2">
			{busy && stage && (
				<div className="flex items-center gap-2 bg-background border rounded-full px-3 py-1.5 shadow text-sm">
					<Loader2 className="animate-spin" size={16} />
					{STAGE_LABEL[stage] ?? stage}
				</div>
			)}
			{error && (
				<div className="bg-destructive text-destructive-foreground rounded-full px-3 py-1.5 shadow text-sm">
					{error}
				</div>
			)}
			{notice && (
				<div
					role="status"
					className="bg-secondary text-secondary-foreground rounded-full px-3 py-1.5 shadow text-sm max-w-xs text-right"
				>
					{notice}
				</div>
			)}
			<Button
				variant={recording ? "destructive" : "default"}
				size="icon"
				className="rounded-full h-14 w-14 shadow-2xl cursor-pointer"
				onClick={handleClick}
				disabled={busy}
				title="Add by voice"
			>
				{recording ? <MicOff size={24} /> : <Mic size={24} />}
			</Button>
		</div>
	);
};

export { VoiceAddButton };
